import React, { useEffect, useState, useMemo } from "react";
import { useHistoryBack } from "../lib/useHistoryBack";
import { VolverButton } from "../components/VolverButton";
import { Link } from "react-router-dom";
import api, { formatMoney, formatNumber } from "../lib/api";
import {
  Loader2, ShoppingCart, Plus, Package, Calendar, AlertTriangle,
  RefreshCcw, ExternalLink, X, Sparkles,
} from "lucide-react";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";

const QUICK_HORIZONS = [15, 30, 45, 60, 90, 120];

const fmtD = (v) => (v ? new Date(v).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

export default function Reaprovisionamientos() {
  const [suppliers, setSuppliers] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { kind: 'supplier'|'lab', id, name }
  const [horizon, setHorizon] = useState(60);
  const [proposal, setProposal] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [topMode, setTopMode] = useState(false);

  // Family panel
  const [famPanel, setFamPanel] = useState(null);
  const famNav = useHistoryBack(!!famPanel, () => setFamPanel(null));
  const [famLoading, setFamLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: sups } = await api.get("/suppliers");
        setSuppliers(sups);
        const latestId = sups[0]?.latest_study_id;
        if (latestId) {
          const { data: lbs } = await api.get(`/studies/${latestId}/labs-summary`);
          setLabs(lbs);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const latestStudyId = suppliers[0]?.latest_study_id;
  const latestStudyName = suppliers[0]?.latest_study_name;

  const generate = async (sel, h) => {
    if (!sel || !h || !latestStudyId) return;
    setGenerating(true);
    setProposal(null);
    try {
      let data;
      if (sel.kind === "supplier") {
        ({ data } = await api.post(`/suppliers/${sel.id}/order-builder`, {
          study_id: latestStudyId,
          horizon_days: Number(h),
        }));
      } else {
        ({ data } = await api.post(`/studies/${latestStudyId}/lab-order-builder`, {
          lab: sel.name,
          horizon_days: Number(h),
        }));
      }
      setProposal(data);
      setTimeout(() => document.getElementById("order-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } finally { setGenerating(false); }
  };

  const pickItem = (item) => {
    setSelected(item);
    generate(item, horizon);
  };
  const pickHorizon = (h) => {
    setHorizon(h);
    if (selected) generate(selected, h);
  };

  const openFamily = async (familia) => {
    if (!familia || !latestStudyId) return;
    setFamLoading(true);
    setFamPanel({ familia, items: [], count: 0, total_value_pvp: 0 });
    try {
      const { data } = await api.get(`/studies/${latestStudyId}/family-parados`, { params: { familia } });
      setFamPanel(data);
    } finally { setFamLoading(false); }
  };

  // Merge suppliers + labs into a single unified list of "providers"
  const supplierLabNames = useMemo(() => {
    // Don't duplicate labs that match supplier names (or supplier catalogs)
    return new Set(suppliers.map((s) => s.name.toLowerCase()));
  }, [suppliers]);

  const rows = useMemo(() => {
    const supRows = suppliers.map((s) => ({
      key: `sup-${s.id}`,
      kind: "supplier",
      id: s.id,
      name: s.name,
      tipo: s.is_direct ? "Directo" : "Proveedor",
      catalog_count: s.catalog_count ?? 0,
      parado_total_pvp: s.parado_total_pvp ?? 0,
      parado_count: s.parado_count ?? 0,
      discount_pct: s.discount_pct ?? null,
      min_order_eur: s.min_order_eur ?? null,
    }));
    const labRows = labs
      .filter((l) => !supplierLabNames.has(l.lab.toLowerCase()))
      .map((l) => ({
        key: `lab-${l.lab}`,
        kind: "lab",
        id: l.lab,
        name: l.lab,
        tipo: "Laboratorio",
        catalog_count: l.products_count,
        parado_total_pvp: l.parado_total_pvp,
        parado_count: l.parado_count,
        discount_pct: null,
        min_order_eur: null,
      }));
    return [...supRows, ...labRows];
  }, [suppliers, labs, supplierLabNames]);

  const filtered = useMemo(() => {
    let arr = rows;
    if (topMode) {
      arr = [...arr].filter((r) => r.parado_total_pvp > 0).sort((a, b) => b.parado_total_pvp - a.parado_total_pvp).slice(0, 20);
    }
    const q = search.trim().toLowerCase();
    if (q) arr = arr.filter((r) => r.name.toLowerCase().includes(q));
    return arr;
  }, [rows, search, topMode]);

  const sort = useSortable(filtered, "parado_total_pvp", "desc");

  // Executive summary metrics
  const summary = useMemo(() => {
    const withParado = rows.filter((r) => r.parado_total_pvp > 0);
    const top20 = [...withParado].sort((a, b) => b.parado_total_pvp - a.parado_total_pvp).slice(0, 20);
    const top20Sum = top20.reduce((s, r) => s + r.parado_total_pvp, 0);
    const totalSum = withParado.reduce((s, r) => s + r.parado_total_pvp, 0);
    const top20Refs = top20.reduce((s, r) => s + (r.parado_count || 0), 0);
    return {
      top20Sum,
      top20Refs,
      top20Count: top20.length,
      totalSum,
      totalLabs: withParado.length,
      recovery50: top20Sum * 0.5,
    };
  }, [rows]);
  const [ajPed, setAjPed] = useState({});
  useEffect(() => { setAjPed({}); }, [proposal]);
  const ord = useMemo(() => {
    const o = proposal?.order;
    if (!o) return null;
    const disc = (o.discount_pct || 0) / 100;
    const items = o.items.map((it) => {
      const pedido = ajPed[it.cn] ?? it.pedido;
      const coste = Math.round(pedido * (it.pmc || 0) * 100) / 100;
      return { ...it, pedido, propuesto: it.pedido, coste, coste_propuesto: it.coste, neto: Math.round(coste * (1 - disc) * 100) / 100 };
    });
    const activos = items.filter((i) => i.pedido > 0);
    const total_net = activos.reduce((t, i) => t + i.neto, 0);
    const min = proposal?.supplier?.min_order_eur;
    return { ...o, items, lines: activos.length, units: activos.reduce((t, i) => t + i.pedido, 0),
             total_cost: activos.reduce((t, i) => t + i.coste, 0), total_net, meets_minimum: min == null || total_net >= min };
  }, [proposal, ajPed]);
  const orderSort = useSortable(ord?.items || [], "coste_propuesto", "desc");
  const paradoSort = useSortable(proposal?.parado?.items || [], "valor_pvp", "desc");
  const famSort = useSortable(famPanel?.items || [], "valor_pvp", "desc");

  if (loading) return <div className="p-8 flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={16}/> Cargando…</div>;

  return (
    <div className="p-8 fade-in" data-testid="reaprovisionamientos-page">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Reaprovisionamientos</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1">Pedido directo + canje</h1>
          <p className="text-slate-600 mt-1 max-w-2xl">Elige un proveedor, elige el plazo, listo. Calculado sobre el último estudio.</p>
        </div>
        <Link to="/suppliers" className="btn btn-outline btn-sm" data-testid="manage-suppliers-link">
          <Plus size={14}/> Gestionar proveedores y catálogos
        </Link>
      </div>

      {latestStudyName && (
        <a href={`/studies/${latestStudyId}`} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 transition rounded-full text-xs"
           data-testid="study-badge" title="Abrir el estudio en una pestaña nueva">
          <span className="text-slate-500 font-semibold uppercase tracking-widest">Estudio:</span>
          <span className="font-bold text-slate-900">{latestStudyName}</span>
          <span className="text-emerald-600 font-semibold">· más reciente</span>
          <ExternalLink size={11} className="text-slate-400"/>
        </a>
      )}

      {/* Resumen ejecutivo · cementerio de stock */}
      {summary.totalSum > 0 && (
        <div className="card mb-6 overflow-hidden" data-testid="exec-summary">
          <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 px-6 py-3 text-white">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-100">Tu cementerio de stock</div>
            <h2 className="font-display text-xl font-extrabold">
              El TOP 20 vale <span className="text-white">{formatMoney(summary.top20Sum)}</span> a PVP
            </h2>
            <p className="text-amber-50 text-sm mt-1">
              Con una negociación al <b>50 %</b> recuperarías <b className="text-white">~{formatMoney(summary.recovery50)}</b>.
              Estás dejando dinero parado en {summary.top20Count} laboratorios ({summary.top20Refs} referencias).
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100 border-t border-slate-100">
            <div className="p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">TOP 20 · valor PVP</div>
              <div className="font-display text-xl font-extrabold text-amber-700">{formatMoney(summary.top20Sum)}</div>
            </div>
            <div className="p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Recuperable al 50 %</div>
              <div className="font-display text-xl font-extrabold text-emerald-700">{formatMoney(summary.recovery50)}</div>
            </div>
            <div className="p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total parado (todos labs)</div>
              <div className="font-display text-xl font-extrabold">{formatMoney(summary.totalSum)}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{summary.totalLabs} laboratorios afectados</div>
            </div>
            <div className="p-4 flex items-center">
              <button onClick={() => setTopMode(true)}
                      className="btn btn-primary btn-sm w-full justify-center"
                      data-testid="show-top-20-cta">
                💰 Ver TOP 20 →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paso 1 · Elige proveedor */}
      {rows.length === 0 ? (
        <div className="card p-10 text-center">
          <Package size={32} className="mx-auto text-slate-300 mb-2"/>
          <h3 className="font-display font-bold text-lg">Sube un estudio primero</h3>
          <p className="text-sm text-slate-500 mt-1 mb-4">Necesitas un estudio cargado para ver los laboratorios.</p>
          <Link to="/upload" className="btn btn-primary inline-flex"><Plus size={14}/> Subir estudio</Link>
        </div>
      ) : (
        <div className="card overflow-x-auto mb-6">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Paso 1</div>
              <h2 className="font-display text-lg font-bold">Elige proveedor o laboratorio</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">{suppliers.length} proveedores creados + {labs.filter((l) => !supplierLabNames.has(l.lab.toLowerCase())).length} laboratorios del estudio</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setTopMode(!topMode)}
                      className={"px-3 py-1.5 rounded-full text-xs font-bold border-2 transition flex items-center gap-1 " +
                        (topMode ? "bg-amber-500 text-white border-amber-500 shadow" : "bg-white text-amber-700 border-amber-300 hover:border-amber-500")}
                      data-testid="top-canje-toggle"
                      title="Solo los 20 laboratorios con más stock parado">
                💰 TOP 20 oportunidades de canje
              </button>
              <input type="text" placeholder="Buscar laboratorio…" value={search} onChange={(e) => setSearch(e.target.value)}
                     className="input text-sm" data-testid="search-supplier" style={{minWidth: 200}}/>
              <ExportButtons
                filename="proveedores-parado"
                title="Proveedores y laboratorios · Stock parado (CC + D)"
                sheetName="Proveedores"
                columns={[
                  { header: "Nombre", key: "name" },
                  { header: "Tipo", key: "tipo" },
                  { header: "Productos", key: "catalog_count", align: "right" },
                  { header: "Stock parado total €", key: "parado_total_pvp", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Referencias paradas", key: "parado_count", align: "right" },
                ]}
                rows={sort.sortedRows}
              />
            </div>
          </div>
          <table className="dense" data-testid="reaprovisionamientos-table">
            <thead>
              <tr>
                <th><SortHeader label="Proveedor / Laboratorio" k="name" sort={sort} defaultDir="asc"/></th>
                <th><SortHeader label="Tipo" k="tipo" sort={sort} defaultDir="asc"/></th>
                <th className="num"><SortHeader label="Productos" k="catalog_count" sort={sort} num/></th>
                <th className="num"><SortHeader label="💰 Stock parado total €" k="parado_total_pvp" sort={sort} num/></th>
                <th className="num"><SortHeader label="Ref. paradas" k="parado_count" sort={sort} num/></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sort.sortedRows.map((r) => {
                const isSelected = selected && selected.kind === r.kind && (selected.id === r.id || selected.name === r.name);
                return (
                  <tr key={r.key}
                      onClick={() => pickItem({ kind: r.kind, id: r.id, name: r.name })}
                      className={"cursor-pointer transition " + (isSelected ? "bg-slate-900 text-white hover:bg-slate-800" : "hover:bg-slate-50")}
                      data-testid={`row-${r.key}`}>
                    <td className="font-semibold">{r.name}</td>
                    <td>
                      <span className={"px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest " +
                        (r.kind === "supplier"
                          ? (isSelected ? "bg-emerald-400 text-slate-900" : "bg-emerald-100 text-emerald-700")
                          : (isSelected ? "bg-slate-700 text-slate-200" : "bg-slate-100 text-slate-600"))}>
                        {r.tipo}
                      </span>
                    </td>
                    <td className="num">{formatNumber(r.catalog_count)}</td>
                    <td className="num">
                      {r.parado_total_pvp > 0 ? (
                        <span className={"font-bold " + (isSelected ? "text-amber-300" : "text-amber-700")}>
                          {formatMoney(r.parado_total_pvp)}
                        </span>
                      ) : <span className={isSelected ? "text-slate-400" : "text-slate-300"}>—</span>}
                    </td>
                    <td className="num">{r.parado_count > 0 ? r.parado_count : <span className={isSelected ? "text-slate-400" : "text-slate-300"}>—</span>}</td>
                    <td>
                      {isSelected ? (
                        <span className="text-xs font-bold text-amber-300 flex items-center gap-1">✓ Seleccionado</span>
                      ) : (
                        <span className="text-xs font-semibold text-slate-500 flex items-center gap-1"><ShoppingCart size={12}/> Elegir</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paso 2 · Plazo */}
      {selected && (
        <div className="card p-6 mb-4 border-2 border-slate-900" data-testid="step-horizon">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Paso 2</div>
          <h2 className="font-display text-2xl font-extrabold mb-1 flex items-center gap-2">
            <Calendar size={22} className="text-slate-400"/> ¿Para cuántos días pedir a {selected.name}?
          </h2>
          <p className="text-slate-500 text-sm mb-4">El pedido se recalcula al instante. Estudio: <b>{latestStudyName}</b>.</p>
          <div className="flex flex-wrap gap-2 items-center">
            {QUICK_HORIZONS.map((d) => (
              <button key={d} onClick={() => pickHorizon(d)}
                className={"px-5 py-3 rounded-xl text-sm font-bold border-2 transition " +
                  (Number(horizon) === d ? "bg-slate-900 text-white border-slate-900 shadow-lg" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")}
                data-testid={`horizon-${d}`}>
                {d} días
              </button>
            ))}
            <div className="flex items-center gap-2 ml-2">
              <span className="text-sm text-slate-500">o</span>
              <input type="number" min="1" value={horizon}
                     onChange={(e) => setHorizon(e.target.value)}
                     onBlur={(e) => pickHorizon(Number(e.target.value))}
                     className="input w-24" data-testid="horizon-custom"/>
              <span className="text-sm text-slate-500">días</span>
            </div>
            {generating && <span className="ml-3 text-xs text-slate-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin"/> Calculando…</span>}
          </div>
        </div>
      )}

      {/* Resultado */}
      {proposal && (
        <div id="order-result">
          {/* Resumen */}
          <div className="card p-5 mb-5 !bg-slate-900 text-white">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Resultado · Pedido a {proposal.horizon_days} días · {selected?.name}</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Líneas</div><div className="font-display text-lg font-bold">{ord.lines}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Unidades</div><div className="font-display text-lg font-bold">{formatNumber(ord.units)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Coste bruto</div><div className="font-display text-lg font-bold">{formatMoney(ord.total_cost)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Descuento</div><div className="font-display text-lg font-bold">{(proposal.order.discount_pct || 0).toFixed(1)}%</div></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Neto a pagar</div>
                <div className={"font-display text-lg font-bold " + (ord.meets_minimum ? "text-emerald-400" : "text-red-400")}>
                  {formatMoney(ord.total_net)}
                </div>
                {!ord.meets_minimum && <div className="text-[10px] text-red-300 flex items-center gap-1 mt-1"><AlertTriangle size={10}/> No alcanza el mínimo</div>}
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-700 flex items-center gap-2 text-sm">
              <RefreshCcw size={14} className="text-amber-400"/>
              <span className="text-slate-300">Stock parado de {selected?.name} para canje:</span>
              <span className="font-bold text-amber-300">{formatMoney(proposal.parado.total_value_pvp)}</span>
              <span className="text-slate-400">({proposal.parado.lines} ref.)</span>
            </div>
          </div>

          {/* PEDIDO */}
          <div className="card mb-6">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-display text-lg font-bold flex items-center gap-2">
                <ShoppingCart size={18} className="text-emerald-600"/> Pedido sugerido ({ord.lines} líneas)
              </h2>
              <ExportButtons
                filename={`pedido-${selected?.name}-${proposal.horizon_days}d`}
                title={`Pedido directo · ${selected?.name} · ${proposal.horizon_days} días`}
                subtitle={`${ord.lines} líneas · ${ord.units} uds · Neto ${formatMoney(ord.total_net)}`}
                sheetName="Pedido"
                orientation="landscape"
                columns={[
                  { header: "CN", key: "cn" }, { header: "Descripción", key: "descripcion" },
                  { header: "Familia", key: "familia" },
                  { header: "Productos en familia", key: "family_total", align: "right" },
                  { header: "Parados en familia", key: "family_parado_count", align: "right" },
                  { header: "ABCD", key: "abcd" }, { header: "Stock", key: "stock", align: "right" },
                  { header: "V.diaria", key: "venta_diaria", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Objetivo", key: "objetivo", align: "right" },
                  { header: "Uds vendidas", key: "uds_periodo", align: "right" },
                  { header: "Última venta", key: "ultima_venta", format: (v) => (v ? fmtD(v) : "") },
                  { header: "Última entrada", key: "ultima_entrada", format: (v) => (v ? fmtD(v) : "") },
                  { header: "Pedido", key: "pedido", align: "right" },
                  { header: "PMC", key: "pmc", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Neto", key: "neto", format: (v) => (v||0).toFixed(2), align: "right" },
                ]}
                rows={orderSort.sortedRows.filter((r) => r.pedido > 0)}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="dense text-[11px]" data-testid="order-table">
                <thead>
                  <tr>
                    <th><SortHeader label="CN" k="cn" sort={orderSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Descripción" k="descripcion" sort={orderSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Familia · análisis" k="familia" sort={orderSort} defaultDir="asc"/></th>
                    <th><SortHeader label="ABCD" k="abcd" sort={orderSort} defaultDir="asc"/></th>
                    <th className="num"><SortHeader label="Stock" k="stock" sort={orderSort} num/></th>
                    <th className="num"><SortHeader label="Uds vend." k="uds_periodo" sort={orderSort} num/></th>
                    <th><SortHeader label="Últ. venta" k="ultima_venta" sort={orderSort} defaultDir="desc"/></th>
                    <th><SortHeader label="Últ. entrada" k="ultima_entrada" sort={orderSort} defaultDir="desc"/></th>
                    <th className="num"><SortHeader label="V/día" k="venta_diaria" sort={orderSort} num/></th>
                    <th className="num"><SortHeader label="Pedido" k="pedido" sort={orderSort} num/></th>
                    <th className="num"><SortHeader label="PMC" k="pmc" sort={orderSort} num/></th>
                    <th className="num"><SortHeader label="Neto" k="neto" sort={orderSort} num/></th>
                  </tr>
                </thead>
                <tbody>
                  {orderSort.sortedRows.length === 0 ? (
                    <tr><td colSpan="13" className="text-center text-slate-400 py-8">No hay productos de este proveedor que necesiten reposición para {proposal.horizon_days} días.</td></tr>
                  ) : orderSort.sortedRows.map((it, i) => (
                    <tr key={`o-${it.cn}`} data-testid={`order-row-${it.cn}`}>
                      <td className="font-mono">{it.cn}</td>
                      <td>{it.descripcion}</td>
                      <td className="text-xs">
                        <div className="font-semibold text-slate-700">{it.familia || "—"}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                          <span><b>{it.family_total || 0}</b> productos</span>
                          {(it.family_parado_count || 0) > 0 && (
                            <button onClick={() => openFamily(it.familia)}
                                    className="text-amber-700 hover:text-amber-900 hover:underline font-semibold"
                                    data-testid={`open-family-${it.cn}`}
                                    title="Ver TODOS los parados de esta familia">
                              · <b>{it.family_parado_count}</b> parados →
                            </button>
                          )}
                        </div>
                      </td>
                      <td><span className={`abcd-pill abcd-${it.abcd}`}>{it.abcd}</span></td>
                      <td className="num">{it.stock}</td>
                      <td className="num">{it.uds_periodo ?? "—"}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(it.ultima_venta)}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(it.ultima_entrada)}</td>
                      <td className="num">{it.venta_diaria.toFixed(2)}</td>
                      <td className="num">
                        <input type="number" min={0} value={it.pedido} data-testid={`order-pedido-${it.cn}`}
                               onChange={(e) => { const v = Math.max(0, parseInt(e.target.value, 10) || 0); setAjPed((a) => ({ ...a, [it.cn]: v })); }}
                               className={"w-14 text-right font-bold rounded border px-1.5 py-0.5 " + (it.pedido !== it.propuesto ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200")} />
                      </td>
                      <td className="num">{(it.pmc || 0).toFixed(2)}</td>
                      <td className="num font-bold">{(it.neto || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* INFORME DE CANJE */}
          <div className="card border-2 border-amber-300">
            <div className="px-5 py-4 border-b border-amber-100 bg-amber-50/50 flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-display text-lg font-bold flex items-center gap-2">
                  <RefreshCcw size={18} className="text-amber-600"/> Informe de canje · Stock parado (CC + D) de {selected?.name}
                </h2>
                <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                  Productos de este proveedor <b>parados</b> en tu farmacia. Total disponible para canje:{" "}
                  <b className="text-amber-700">{formatMoney(proposal.parado.total_value_pvp)}</b> a PVP.
                </p>
              </div>
              <ExportButtons
                filename={`canje-${selected?.name}`}
                title={`Stock parado para canje · ${selected?.name}`}
                subtitle={`${proposal.parado.lines} ref. · ${formatMoney(proposal.parado.total_value_pvp)} a PVP`}
                sheetName="Canje"
                columns={[
                  { header: "CN", key: "cn" }, { header: "Descripción", key: "descripcion" },
                  { header: "Familia", key: "familia" }, { header: "ABCD", key: "abcd" },
                  { header: "Stock", key: "stock", align: "right" },
                  { header: "Valor PVP", key: "valor_pvp", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Uds vendidas", key: "uds_vendidas", align: "right" },
                  { header: "Última venta", key: "ultima_venta", format: (v) => (v ? fmtD(v) : "") },
                  { header: "Última entrada", key: "ultima_entrada", format: (v) => (v ? fmtD(v) : "") },
                ]}
                rows={paradoSort.sortedRows}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="dense text-[11px]" data-testid="parado-table">
                <thead>
                  <tr>
                    <th><SortHeader label="CN" k="cn" sort={paradoSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Descripción" k="descripcion" sort={paradoSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Familia" k="familia" sort={paradoSort} defaultDir="asc"/></th>
                    <th><SortHeader label="ABCD" k="abcd" sort={paradoSort} defaultDir="asc"/></th>
                    <th className="num"><SortHeader label="Stock" k="stock" sort={paradoSort} num/></th>
                    <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={paradoSort} num/></th>
                    <th className="num"><SortHeader label="Uds vend." k="uds_vendidas" sort={paradoSort} num/></th>
                    <th><SortHeader label="Últ. venta" k="ultima_venta" sort={paradoSort} defaultDir="desc"/></th>
                    <th><SortHeader label="Últ. entrada" k="ultima_entrada" sort={paradoSort} defaultDir="desc"/></th>
                  </tr>
                </thead>
                <tbody>
                  {paradoSort.sortedRows.length === 0 ? (
                    <tr><td colSpan="9" className="text-center text-slate-400 py-8">No hay stock parado de este proveedor. ¡Buena gestión!</td></tr>
                  ) : paradoSort.sortedRows.map((p, i) => (
                    <tr key={`pd-${i}-${p.cn}`}>
                      <td className="font-mono">{p.cn}</td>
                      <td>{p.descripcion}</td>
                      <td className="text-xs">{p.familia}</td>
                      <td><span className={`abcd-pill abcd-${p.abcd}`}>{p.abcd}</span></td>
                      <td className="num">{p.stock}</td>
                      <td className="num font-bold">{(p.valor_pvp || 0).toFixed(2)}</td>
                      <td className="num">{p.uds_vendidas ?? 0}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(p.ultima_venta)}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(p.ultima_entrada)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Slide-over panel: TODOS los parados de una familia */}
      {famPanel && (
        <div className="fixed inset-0 z-[60] flex" data-testid="family-panel">
          <div className="w-full bg-white shadow-2xl flex flex-col h-full">
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4 bg-amber-50">
              <VolverButton onClick={famNav.back} testid="family-panel-volver" className="mt-1" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Moneda de canje · Familia completa</div>
                <h2 className="font-display text-xl font-extrabold mt-1">{famPanel.familia}</h2>
                {famLoading ? (
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Loader2 size={11} className="animate-spin"/> Cargando…</div>
                ) : (
                  <div className="text-xs text-slate-700 mt-1">
                    <b>{famPanel.count}</b> referencias paradas (CC + D) · valor total a PVP:{" "}
                    <b className="text-amber-700">{formatMoney(famPanel.total_value_pvp)}</b>
                  </div>
                )}
              </div>
              <button onClick={famNav.close} className="p-2 hover:bg-amber-100 rounded-lg" data-testid="close-family-panel">
                <X size={18}/>
              </button>
            </div>
            <div className="px-6 py-3 border-b border-slate-200">
              <ExportButtons
                filename={`canje-familia-${famPanel.familia}`.replace(/[^a-z0-9-]/gi, "_")}
                title={`Stock parado · Familia ${famPanel.familia}`}
                subtitle={`${famPanel.count} ref. · ${formatMoney(famPanel.total_value_pvp)} a PVP`}
                sheetName="Familia"
                columns={[
                  { header: "CN", key: "cn" }, { header: "Descripción", key: "descripcion" },
                  { header: "Laboratorio", key: "laboratorio" },
                  { header: "ABCD", key: "abcd" },
                  { header: "Stock", key: "stock", align: "right" },
                  { header: "PVP", key: "pvp", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Valor PVP", key: "valor_pvp", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Uds vendidas", key: "uds_vendidas", align: "right" },
                  { header: "Última venta", key: "ultima_venta", format: (v) => (v ? fmtD(v) : "") },
                  { header: "Última entrada", key: "ultima_entrada", format: (v) => (v ? fmtD(v) : "") },
                ]}
                rows={famSort.sortedRows}
              />
            </div>
            <div className="flex-1 overflow-auto">
              <table className="dense text-[11px]" data-testid="family-table">
                <thead>
                  <tr>
                    <th><SortHeader label="CN" k="cn" sort={famSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Descripción" k="descripcion" sort={famSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Lab" k="laboratorio" sort={famSort} defaultDir="asc"/></th>
                    <th><SortHeader label="ABCD" k="abcd" sort={famSort} defaultDir="asc"/></th>
                    <th className="num"><SortHeader label="Stock" k="stock" sort={famSort} num/></th>
                    <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={famSort} num/></th>
                    <th className="num"><SortHeader label="Uds vend." k="uds_vendidas" sort={famSort} num/></th>
                    <th><SortHeader label="Últ. venta" k="ultima_venta" sort={famSort} defaultDir="desc"/></th>
                    <th><SortHeader label="Últ. entrada" k="ultima_entrada" sort={famSort} defaultDir="desc"/></th>
                  </tr>
                </thead>
                <tbody>
                  {famLoading ? (
                    <tr><td colSpan="9" className="text-center text-slate-400 py-8"><Loader2 size={14} className="inline animate-spin mr-2"/> Cargando…</td></tr>
                  ) : famSort.sortedRows.length === 0 ? (
                    <tr><td colSpan="9" className="text-center text-slate-400 py-8">Sin parados en esta familia.</td></tr>
                  ) : famSort.sortedRows.map((p) => (
                    <tr key={`fp-${p.cn}`}>
                      <td className="font-mono">{p.cn}</td>
                      <td>{p.descripcion}</td>
                      <td className="text-xs">{p.laboratorio || "—"}</td>
                      <td><span className={`abcd-pill abcd-${p.abcd}`}>{p.abcd}</span></td>
                      <td className="num">{p.stock}</td>
                      <td className="num font-bold">{(p.valor_pvp || 0).toFixed(2)}</td>
                      <td className="num">{p.uds_vendidas ?? 0}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(p.ultima_venta)}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(p.ultima_entrada)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
